import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

/**
 * Self-service TOTP 2FA management (Settings → Security).
 * Talks to the optional-2FA backend: /mfa/status|setup|confirm|disable|recovery-codes/regenerate.
 * 2FA is never enforced — this is purely opt-in.
 */
function TwoFactorAuth({ onStatusChange }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);       // { enabled, confirmedAt, recoveryCodesRemaining }
  const [loading, setLoading] = useState(true);
  // mode: 'view' | 'enrolling' | 'recoveryShown' | 'confirm'
  const [mode, setMode] = useState('view');
  const [confirmAction, setConfirmAction] = useState(null); // 'disable' | 'regenerate'
  const [setup, setSetup] = useState(null);         // { secret, otpAuthUri, qrDataUri }
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/mfa/status');
      setStatus(res.data);
      if (onStatusChange) onStatusChange(res.data);
    } catch {
      setStatus({ enabled: false });
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const reset = () => { setMode('view'); setSetup(null); setCode(''); setMsg(''); setConfirmAction(null); };

  const beginEnroll = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await axiosInstance.post('/mfa/setup');
      setSetup(res.data);
      setMode('enrolling');
    } catch (e) {
      setMsg(e.response?.data?.error || t('TwoFactorAuth.errStartSetup'));
    } finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await axiosInstance.post('/mfa/confirm', { code: code.trim() });
      setRecoveryCodes(res.data.recoveryCodes || []);
      setCode('');
      setMode('recoveryShown');
      loadStatus();
    } catch (e) {
      setMsg(e.response?.data?.error || t('TwoFactorAuth.errInvalidCodeRetry'));
    } finally { setBusy(false); }
  };

  const submitConfirm = async () => {
    setBusy(true); setMsg('');
    try {
      if (confirmAction === 'disable') {
        await axiosInstance.post('/mfa/disable', { code: code.trim() });
        reset(); loadStatus();
      } else if (confirmAction === 'regenerate') {
        const res = await axiosInstance.post('/mfa/recovery-codes/regenerate', { code: code.trim() });
        setRecoveryCodes(res.data.recoveryCodes || []);
        setCode(''); setMode('recoveryShown'); loadStatus();
      }
    } catch (e) {
      setMsg(e.response?.data?.error || t('TwoFactorAuth.errInvalidAuthCode'));
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [t('TwoFactorAuth.recoveryFileContent', { codes: recoveryCodes.join('\n') })],
      { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'cataam-recovery-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  const errStyle = {
    padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.8125rem', marginTop: 8,
    background: '#fee2e2', color: '#dc2626',
  };

  if (loading) return <div className="loading-spinner">{t('TwoFactorAuth.loading')}</div>;

  const enabled = !!status?.enabled;

  return (
    <div className="settings-group">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h3 style={{ textAlign: 'left', margin: 0 }}>{t('TwoFactorAuth.heading')}</h3>
        <span className={`key-status-badge ${enabled ? 'active' : 'revoked'}`}>
          {enabled ? t('TwoFactorAuth.statusEnabled') : t('TwoFactorAuth.statusNotEnabled')}
        </span>
      </div>
      <p style={{ fontSize: '0.875rem', color: '#64748b', margin: '0 0 1rem 0', textAlign: 'left' }}>
        {t('TwoFactorAuth.intro')}
      </p>

      {/* ── VIEW ── */}
      {mode === 'view' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          {!enabled ? (
            <button className="primary-btn" onClick={beginEnroll} disabled={busy}>
              <i className="fas fa-shield-alt" /> {busy ? t('TwoFactorAuth.btnStarting') : t('TwoFactorAuth.btnEnable')}
            </button>
          ) : (
            <>
              <p style={{ fontSize: '0.8125rem', color: '#475569', margin: 0, textAlign: 'left' }}>
                {t('TwoFactorAuth.recoveryCodesRemaining', { count: status.recoveryCodesRemaining ?? 0 })}
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="secondary-btn"
                  onClick={() => { setConfirmAction('regenerate'); setMode('confirm'); setMsg(''); }}>
                  <i className="fas fa-sync" /> {t('TwoFactorAuth.btnRegenerateCodes')}
                </button>
                <button className="secondary-btn" style={{ color: '#dc2626', borderColor: '#fecaca' }}
                  onClick={() => { setConfirmAction('disable'); setMode('confirm'); setMsg(''); }}>
                  <i className="fas fa-times-circle" /> {t('TwoFactorAuth.btnDisable')}
                </button>
              </div>
            </>
          )}
          {msg && <div style={errStyle}>{msg}</div>}
        </div>
      )}

      {/* ── ENROLLING: scan QR + confirm code ── */}
      {mode === 'enrolling' && setup && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 460, alignItems: 'flex-start' }}>
          <p style={{ fontSize: '0.875rem', color: '#334155', margin: 0, textAlign: 'left' }}>
            {t('TwoFactorAuth.enrollStep1')}
          </p>
          <img src={setup.qrDataUri} alt={t('TwoFactorAuth.qrCodeAlt')}
            style={{ width: 180, height: 180, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }} />
          <p style={{ fontSize: '0.8125rem', color: '#64748b', margin: 0, textAlign: 'left' }}>
            {t('TwoFactorAuth.enrollManualKey')}
          </p>
          <code className="api-key-display" style={{ wordBreak: 'break-all' }}>{setup.secret}</code>
          <div className="form-group" style={{ width: '100%' }}>
            <label>{t('TwoFactorAuth.enrollStep2Label')}</label>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder={t('TwoFactorAuth.codePlaceholder')}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && confirmEnroll()} />
          </div>
          {msg && <div style={errStyle}>{msg}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="secondary-btn" onClick={reset} disabled={busy}>{t('TwoFactorAuth.btnCancel')}</button>
            <button className="primary-btn" onClick={confirmEnroll} disabled={busy || code.length !== 6}>
              {busy ? t('TwoFactorAuth.btnVerifying') : t('TwoFactorAuth.btnVerifyEnable')}
            </button>
          </div>
        </div>
      )}

      {/* ── CONFIRM (disable / regenerate) ── */}
      {mode === 'confirm' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 380, alignItems: 'flex-start' }}>
          <p style={{ fontSize: '0.875rem', color: '#334155', margin: 0, textAlign: 'left' }}>
            {confirmAction === 'disable'
              ? t('TwoFactorAuth.confirmDisablePrompt')
              : t('TwoFactorAuth.confirmRegeneratePrompt')}
          </p>
          <div className="form-group" style={{ width: '100%' }}>
            <label>{t('TwoFactorAuth.authCodeLabel')}</label>
            <input type="text" inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={e => setCode(e.target.value)} placeholder={t('TwoFactorAuth.authCodePlaceholder')}
              onKeyDown={e => e.key === 'Enter' && code.trim() && submitConfirm()} />
          </div>
          {msg && <div style={errStyle}>{msg}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="secondary-btn" onClick={reset} disabled={busy}>{t('TwoFactorAuth.btnCancel')}</button>
            <button className="primary-btn" onClick={submitConfirm} disabled={busy || !code.trim()}
              style={confirmAction === 'disable' ? { background: '#dc2626' } : {}}>
              {busy ? t('TwoFactorAuth.btnWorking') : confirmAction === 'disable' ? t('TwoFactorAuth.btnDisable') : t('TwoFactorAuth.btnRegenerateCodesShort')}
            </button>
          </div>
        </div>
      )}

      {/* ── RECOVERY CODES (shown once) ── */}
      {mode === 'recoveryShown' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 460, alignItems: 'flex-start' }}>
          <div style={{
            padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.8125rem',
            background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
          }}>
            <strong>{t('TwoFactorAuth.recoveryWarningTitle')}</strong> {t('TwoFactorAuth.recoveryWarningBody')}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, width: '100%',
          }}>
            {recoveryCodes.map(c => (
              <code key={c} style={{ fontSize: '0.95rem', letterSpacing: 1 }}>{c}</code>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="secondary-btn" onClick={copyCodes}>
              <i className={`fas fa-${copied ? 'check' : 'copy'}`} /> {copied ? t('TwoFactorAuth.btnCopied') : t('TwoFactorAuth.btnCopy')}
            </button>
            <button className="secondary-btn" onClick={downloadCodes}>
              <i className="fas fa-download" /> {t('TwoFactorAuth.btnDownload')}
            </button>
            <button className="primary-btn" onClick={reset}>{t('TwoFactorAuth.btnDone')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TwoFactorAuth;
